import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeaturesContext";
import {
  ArrowLeft, BookmarkPlus, CheckCheck, CheckCircle2, ChevronDown, Download,
  FileSpreadsheet, History, Loader2, Play, Plus, Sparkles, Trash2, Upload, XCircle,
} from "lucide-react";

const errMsg = (err, fallback) => formatApiError(err?.response?.data?.detail) || fallback;
const fmtDT = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

const SOURCE_BADGE = {
  ai: ["AI", "bg-accent-subtle text-accent-strong"],
  suggested: ["suggerito", "bg-subtle text-text-secondary"],
  preset: ["set salvato", "bg-subtle text-text-secondary"],
};

const RuleRow = ({ r, onRemove }) => {
  const [label, cls] = SOURCE_BADGE[r.source] || SOURCE_BADGE.ai;
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
         data-testid={`rule-item-${r.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${cls}`}>{label}</span>
        <span className="text-sm text-text-primary truncate">{r.description}</span>
      </div>
      <button type="button" onClick={() => onRemove(r.id)} data-testid={`rule-remove-${r.id}`}
              className="text-text-muted hover:text-red-600 transition-colors flex-shrink-0">
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const ResultRow = ({ x, n }) => {
  const [open, setOpen] = useState(false);
  const r = x.rule;
  const failed = !x.skipped && x.violations > 0;
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${failed ? "border-red-200 bg-red-50/50" : "border-border"}`}
         data-testid={`report-rule-${r.id}`}>
      <button type="button" className="w-full flex items-center justify-between gap-3 text-left"
              onClick={() => failed && setOpen((o) => !o)}>
        <div className="flex items-center gap-2 min-w-0">
          {x.skipped ? <XCircle size={15} className="text-text-muted flex-shrink-0" />
            : failed ? <XCircle size={15} className="text-red-600 flex-shrink-0" />
            : <CheckCircle2 size={15} className="text-success flex-shrink-0" />}
          <span className="text-sm text-text-primary truncate">Regola {n}: {r.description}</span>
        </div>
        <span className="text-[12px] flex-shrink-0 flex items-center gap-1.5">
          {x.skipped ? <span className="text-text-muted">{x.note}</span>
            : failed ? (
              <>
                <strong className="text-red-700">{x.violations.toLocaleString("it-IT")} violazioni</strong>
                <span className="text-text-muted">su {x.checked.toLocaleString("it-IT")}</span>
                <ChevronDown size={13} className={`text-text-muted transition-transform ${open ? "rotate-180" : ""}`} />
              </>
            ) : <span className="text-success font-medium">OK ({x.checked.toLocaleString("it-IT")} controllate)</span>}
        </span>
      </button>
      {open && failed && (
        <div className="mt-2 overflow-x-auto" data-testid={`report-sample-${r.id}`}>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-text-muted border-b border-border">
                <th className="py-1 pr-3 font-medium">Riga</th>
                {Object.keys(x.sample[0]?.values || {}).map((c) => (
                  <th key={c} className="py-1 pr-3 font-medium">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {x.sample.map((s) => (
                <tr key={s.row} className="border-b border-border/50 last:border-0">
                  <td className="py-1 pr-3 font-mono text-text-muted">{s.row}</td>
                  {Object.entries(s.values).map(([c, v]) => (
                    <td key={c} className="py-1 pr-3 font-mono text-text-primary">{v || "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {x.violations > x.sample.length && (
            <p className="text-[10px] text-text-muted mt-1">
              Prime {x.sample.length} righe: scarica l'Excel per l'elenco completo.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default function QualityCheck() {
  const { user } = useAuth();
  const { features } = useFeatures();
  const [sid, setSid] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [rules, setRules] = useState([]);
  const [unsupported, setUnsupported] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [report, setReport] = useState(null);
  const [rulesets, setRulesets] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null); // upload|translate|run|save
  const [savingName, setSavingName] = useState("");
  const [showSave, setShowSave] = useState(false);
  const fileRef = useRef(null);

  const allowed = user?.role === "super_admin" || features?.ai_tools;
  const [historyFailed, setHistoryFailed] = useState(false);
  const loadSide = () => {
    api.get("/ai/tools/quality/sessions").then(({ data }) => { setHistory(data); setHistoryFailed(false); }).catch(() => setHistoryFailed(true));
    api.get("/ai/tools/quality/rulesets").then(({ data }) => setRulesets(data)).catch(() => {});
  };
  useEffect(() => { if (allowed) loadSide(); }, [allowed]);

  if (features && !allowed) return <Navigate to="/services" replace />;

  const reset = () => {
    setSid(null); setFileInfo(null); setRules([]); setUnsupported([]);
    setInstructions(""); setReport(null); setShowSave(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onUpload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      let id = sid;
      if (!id || report) {
        const { data } = await api.post("/ai/tools/quality/sessions");
        id = data.id;
        setSid(id);
        setReport(null);
      }
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/ai/tools/quality/sessions/${id}/file`, fd);
      setFileInfo(data.file);
      setRules(data.suggested_rules || []);
      setUnsupported([]);
      setReport(null);
      if ((data.suggested_rules || []).length > 0)
        toast.success(`${data.suggested_rules.length} controlli proposti dalla struttura del file`);
    } catch (err) {
      toast.error(errMsg(err, "Caricamento non riuscito"));
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onTranslate = async () => {
    setBusy("translate");
    try {
      const { data } = await api.post(`/ai/tools/quality/sessions/${sid}/translate`,
        { instructions });
      const existing = new Set(rules.map((r) => r.description));
      setRules((rs) => [...rs, ...data.rules.filter((r) => !existing.has(r.description))]);
      setUnsupported(data.unsupported || []);
      if (data.rules.length === 0) toast.error("Nessuna regola traducibile: riformula le richieste");
      else toast.success(`${data.rules.length} ${data.rules.length === 1 ? "regola tradotta" : "regole tradotte"} — verificale e conferma`);
      setInstructions("");
    } catch (err) {
      toast.error(errMsg(err, "Traduzione non riuscita"));
    } finally { setBusy(null); }
  };

  const onRun = async (rulesToRun = rules) => {
    setBusy("run");
    try {
      const { data } = await api.post(`/ai/tools/quality/sessions/${sid}/run`,
        { rules: rulesToRun });
      setReport(data);
      loadSide();
    } catch (err) {
      toast.error(errMsg(err, "Esecuzione non riuscita"));
    } finally { setBusy(null); }
  };

  const onSaveRuleset = async () => {
    setBusy("save");
    try {
      await api.post("/ai/tools/quality/rulesets", { name: savingName, rules });
      toast.success(`Set di controlli «${savingName}» salvato: la prossima volta un click e via`);
      setShowSave(false); setSavingName("");
      loadSide();
    } catch (err) {
      toast.error(errMsg(err, "Salvataggio non riuscito"));
    } finally { setBusy(null); }
  };

  const applyRuleset = (rs) => {
    setRules(rs.rules.map((r) => ({ ...r, source: "preset" })));
    setUnsupported([]);
    toast.success(`Set «${rs.name}» caricato (${rs.rules.length} controlli) — esecuzione gratuita`);
  };

  const deleteRuleset = async (id) => {
    try {
      await api.delete(`/ai/tools/quality/rulesets/${id}`);
      loadSide();
    } catch (err) { toast.error(errMsg(err, "Eliminazione non riuscita")); }
  };

  const openHistory = async (id) => {
    try {
      const { data } = await api.get(`/ai/tools/quality/sessions/${id}`);
      setSid(data.id); setFileInfo(data.file);
      const rep = data.report;
      setRules((rep?.results || []).map((x) => x.rule));
      setReport(rep ? { ...rep, id: data.id } : null);
      setUnsupported([]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(errMsg(err, "Sessione non trovata")); }
  };

  const downloadExcel = async () => {
    try {
      const { data } = await api.get(`/ai/tools/quality/sessions/${sid}/report.xlsx`,
        { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `controlli_${(fileInfo?.filename || "file").replace(/\.[^.]+$/, "")}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) { toast.error(errMsg(err, "Download non riuscito")); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="quality-page">
      <div>
        <Link to="/assistant" className="inline-flex items-center gap-1.5 text-[13px] text-text-secondary hover:text-text-primary transition-colors mb-4"
              data-testid="quality-back">
          <ArrowLeft size={14} /> Assistente AI
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-medium tracking-tight text-text-primary flex items-center gap-2">
              <CheckCheck size={22} className="text-accent-strong" /> Controlli qualità dati
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              Scrivi i controlli in italiano, confermali in chiaro, eseguili quando vuoi. I set salvati si riusano con un click.
            </p>
          </div>
          {fileInfo && (
            <button type="button" onClick={reset} className="wl-btn wl-btn--ghost wl-btn--sm"
                    data-testid="quality-new-btn">
              <Plus size={14} className="mr-1" /> Nuovo controllo
            </button>
          )}
        </div>
      </div>

      {/* Step 1: file */}
      <div className="bg-surface border border-border rounded-xl p-5 space-y-3">
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
               onChange={(e) => onUpload(e.target.files?.[0])} data-testid="quality-file-input" />
        {!fileInfo ? (
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy === "upload"}
                  data-testid="quality-upload-btn"
                  className="w-full border-2 border-dashed border-border rounded-xl py-8 flex flex-col items-center gap-2 text-text-secondary hover:border-accent hover:text-text-primary transition-colors">
            {busy === "upload" ? <Loader2 size={22} className="animate-spin" /> : <Upload size={22} />}
            <span className="text-sm font-medium">Carica Excel o CSV</span>
            <span className="text-[11px] text-text-muted">Zeri iniziali preservati · max 10 MB</span>
          </button>
        ) : (
          <div className="flex items-center gap-2.5 text-sm flex-wrap" data-testid="quality-file-chip">
            <FileSpreadsheet size={16} className="text-accent-strong" />
            <span className="font-medium text-text-primary">{fileInfo.filename}</span>
            <span className="text-[11px] text-text-muted">
              {fileInfo.n_rows?.toLocaleString("it-IT")} righe · {fileInfo.n_cols} colonne
            </span>
            <button type="button" className="text-[11px] text-accent-strong hover:underline"
                    onClick={() => fileRef.current?.click()} data-testid="quality-replace-file">
              sostituisci
            </button>
          </div>
        )}
        {fileInfo && rulesets.length > 0 && !report && (
          <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="quality-rulesets-bar">
            <span className="text-[11px] text-text-muted">Set salvati:</span>
            {rulesets.map((rs) => (
              <span key={rs.id} className="inline-flex items-center gap-1">
                <button type="button" onClick={() => applyRuleset(rs)}
                        data-testid={`ruleset-apply-${rs.id}`}
                        className="text-[11px] px-2 py-1 rounded-full border border-border hover:border-accent text-text-primary transition-colors">
                  {rs.name} · {rs.rules.length}
                </button>
                <button type="button" onClick={() => deleteRuleset(rs.id)}
                        data-testid={`ruleset-delete-${rs.id}`}
                        className="text-text-muted hover:text-red-600"><Trash2 size={11} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Step 2: regole */}
      {fileInfo && !report && (
        <div className="bg-surface border border-border rounded-xl p-5 space-y-4" data-testid="quality-rules-card">
          <div>
            <h2 className="text-base font-medium text-text-primary">Definisci i controlli in italiano</h2>
            <p className="text-[12px] text-text-muted mt-0.5">
              Es.: «il POD deve avere 14 caratteri», «la data attivazione non può precedere la data firma»,
              «l'importo non può essere negativo». La traduzione usa 1 operazione AI; l'esecuzione è gratuita.
            </p>
          </div>
          <div className="flex gap-2">
            <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)}
                      rows={2} data-testid="quality-rules-input"
                      placeholder="Scrivi una o più regole, anche su righe separate…"
                      className="flex-1 text-sm bg-surface border border-border rounded-lg px-3 py-2 focus:outline-none focus:border-accent resize-y" />
            <button type="button" onClick={onTranslate}
                    disabled={busy === "translate" || instructions.trim().length < 3}
                    className="wl-btn wl-btn--secondary self-start" data-testid="translate-btn">
              {busy === "translate" ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />}
              Traduci le regole
            </button>
          </div>
          {unsupported.length > 0 && (
            <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                 data-testid="quality-unsupported">
              {unsupported.map((u, i) => (
                <div key={i}>Non traducibile: «{u.instruction}» — {u.reason}</div>
              ))}
            </div>
          )}

          {rules.length > 0 && (
            <>
              <div className="space-y-1.5" data-testid="quality-rules-list">
                {rules.map((r) => (
                  <RuleRow key={r.id} r={r}
                           onRemove={(id) => setRules((rs) => rs.filter((x) => x.id !== id))} />
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-[12px] text-text-muted">
                  {rules.length} {rules.length === 1 ? "controllo confermando" : "controlli"} — rimuovi quelli che non vuoi, poi esegui
                </span>
                <button type="button" onClick={() => onRun()} disabled={busy === "run"}
                        className="wl-btn wl-btn--primary" data-testid="run-btn">
                  {busy === "run" ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Play size={14} className="mr-1.5" />}
                  Esegui i controlli
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 3: report */}
      {report && (
        <div className="bg-surface border border-border rounded-xl p-5 space-y-4" data-testid="quality-report">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-base font-medium text-text-primary">Esito dei controlli</h2>
              <p className="text-[12px] text-text-secondary mt-0.5" data-testid="quality-report-totals">
                {report.totals.passed} OK · {report.totals.failed} con violazioni su{" "}
                {report.totals.run} controlli — {report.totals.total_rows.toLocaleString("it-IT")} righe
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={downloadExcel} className="wl-btn wl-btn--ghost wl-btn--sm"
                      data-testid="download-excel">
                <Download size={13} className="mr-1" /> Excel violazioni
              </button>
              <button type="button" onClick={() => setShowSave((v) => !v)}
                      className="wl-btn wl-btn--secondary wl-btn--sm" data-testid="save-ruleset-btn">
                <BookmarkPlus size={13} className="mr-1" /> Salva come set
              </button>
              <button type="button" onClick={() => setReport(null)}
                      className="wl-btn wl-btn--ghost wl-btn--sm" data-testid="edit-rules-btn">
                Modifica regole
              </button>
            </div>
          </div>
          {showSave && (
            <div className="flex gap-2 items-center" data-testid="quality-save-row">
              <input value={savingName} onChange={(e) => setSavingName(e.target.value)}
                     placeholder="Nome del set (es. Controlli anagrafica POD)"
                     data-testid="ruleset-name-input"
                     className="flex-1 h-9 text-sm bg-surface border border-border rounded-lg px-3 focus:outline-none focus:border-accent" />
              <button type="button" onClick={onSaveRuleset}
                      disabled={busy === "save" || savingName.trim().length < 2}
                      className="wl-btn wl-btn--primary wl-btn--sm" data-testid="ruleset-save-confirm">
                {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : "Salva"}
              </button>
            </div>
          )}
          <div className="space-y-1.5">
            {report.results.map((x, i) => <ResultRow key={x.rule.id} x={x} n={i + 1} />)}
          </div>
        </div>
      )}

      {/* Storico */}
      <div className="bg-surface border border-border rounded-xl p-5" data-testid="quality-history">
        <h2 className="text-base font-medium text-text-primary flex items-center gap-2 mb-3">
          <History size={15} className="text-text-muted" /> Storico controlli
        </h2>
        {historyFailed ? (
          <LoadErrorBox compact what="lo storico controlli" onRetry={loadSide} testId="quality-history-error" />
        ) : history.length === 0 ? (
          <p className="text-sm text-text-muted">Nessun controllo ancora: carica il primo file qui sopra.</p>
        ) : (
          <div className="divide-y divide-border">
            {history.map((h) => (
              <button key={h.id} type="button" onClick={() => openHistory(h.id)}
                      data-testid={`quality-history-item-${h.id}`}
                      className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-subtle rounded px-2 transition-colors">
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileSpreadsheet size={14} className="text-text-muted flex-shrink-0" />
                  <span className="text-sm text-text-primary truncate">{h.filename}</span>
                  <span className="text-[11px] text-text-muted flex-shrink-0">{h.n_rows?.toLocaleString("it-IT")} righe</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-[11px] text-text-muted">
                  {h.totals ? (
                    h.totals.failed > 0
                      ? <span className="text-red-700 font-medium">{h.totals.failed} con violazioni</span>
                      : <span className="text-success font-medium">tutto OK</span>
                  ) : <span className="text-amber-600">da eseguire</span>}
                  <span>{fmtDT(h.created_at)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
